<template>
	<li
		v-if="lineItem.ManufacturerName != 'Freight'"
		class="relative"
		:class="{
			'relative py-6 border-b border-b-gray-lighter flex': true
		}"
	>
		<div class="shrink-0 w-[7.5rem] overflow-hidden bg-white mr-4 md:mr-6">
			<nuxt-img
				:src="itemImage"
				:alt="itemRef?.Name || ''"
				sizes="120px"
				class="h-full w-full object-contain object-center"
				:provider="itemRef.ImageKey || imageUrl ? undefined : 'storyblok'"
			/>
		</div>
		<div>
			<div>
				<nuxt-link
					:to="routeToProduct"
					class="text-md text-secondary font-medium mb-2 block"
				>
					{{ itemRef.Name }}
				</nuxt-link>
			</div>
			<div class="gap-2 text-sm mb-4 flex">
				<Price :old-price="oldPrice" :price="itemRef.PriceDisplayIncVat!" />
			</div>

			<template v-if="itemType === 'product-minicart'">
				<ProductProductAttributesAddToCartQuantitySelector
					v-model="quantity"
					class="!h-[38px] !w-[108px] mt-4"
					:min="1"
					:max="maxQuantity"
					:disabled="getIsLoading"
					@update:modelValue="updateItem()"
				/>
			</template>
		</div>
		<TrashIcon
			v-if="itemType === 'product-minicart'"
			class="shrink-0 cursor-pointer text-rimary ml-auto h-6 w-6"
			@click="removeItemFromCart"
		/>
	</li>
</template>

<script setup lang="ts">
import { TrashIcon } from "@heroicons/vue/24/outline";
import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import Price from "../Product/ProductAttributes/Price.vue";
import { useGtmEvents } from "~/composables/useGtmEvents";

interface Props {
	lineItem: Partial<BasketItem>;
	itemType: string;
	imageUrl?: string;
}

const { lineItem, itemType, imageUrl } = defineProps<Props>();

const config = useRuntimeConfig();
const cartStore = useCartStore();
const { getProductStock, getProductByUniqueName } = useNorcePwa();
const { stores } = storeToRefs(useWarehousesAndStores());
const { locale } = useI18n();
const norceStore = useNorceStore();
const { getIsLoading } = storeToRefs(useHelpersStore());
const localePath = useLocalePath();
const { removeFromCart } = useGtmEvents();

const ImagesCdnUrl = config.public.norce.cdn;
const itemRef = toRef(() => lineItem);
const { oldPrice } = useProductPrices(itemRef.value);

const productStore = useProductStore();
const itemImage = computed(() => {
	if (imageUrl) {
		return imageUrl;
	} else
		return imageUrl
			? `${ImagesCdnUrl}/${imageUrl}?format=webp&w=120`
			: lineItem?.ImageKey
				? `${ImagesCdnUrl}/${lineItem.ImageKey}?format=webp&w=120`
				: productStore.defaultProductImage;
});
const routeToProduct = computed(() =>
	localePath(`/${itemRef.value?.UniqueName}`)
);

const quantity = ref<number>(itemRef.value?.Quantity ?? 0);
const maxQuantity = ref(0);
const updateItem = async () => {
	cartStore?.updateCartItem(
		itemRef.value.LineNo!,
		itemRef.value.PartNo!,
		quantity.value!
	);
};

onMounted(async () => {
	const storesIds = stores?.value?.[locale.value]
		?.map((store) => store.Id.toString())
		.join(",");
	const product = await getProductByUniqueName({
		uniqueName: itemRef.value.UniqueName!,
		storeSeed: storesIds ?? "",
		...norceStore.storeContext
	});

	maxQuantity.value = getProductStock(product);
});

const removeItemFromCart = () => {
	removeFromCart(itemRef.value);
	cartStore?.removeFromCart(itemRef.value.LineNo!);
};

watch(itemRef, () => {
	quantity.value = itemRef.value.Quantity ?? 0;
});
</script>
